'use client';

import './table.css';
import type { BodyRow, EditedRow, HeadField, TableData, TableDataProps } from '@/src/types/table-data.types';
import { useState, forwardRef, useImperativeHandle, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { InputExt } from '../Input/Input';
import { getMasterRow } from '@/src/utils/table-data';

export interface TableRef {
  clearSelection: () => void;
  clearVisualSelection: () => void;
  selectRow: (index: number) => void;
  setRow: (index: number) => void;
  preserveScrollOnPrepend: () => void;
  preserveScrollOnRemoveTop: () => void;
}

const EMPTY_TABLE_DATA: TableData = { body: [], config: { head: [], primaryKey: [] } };
const EMPTY_EDITED_ROW: EditedRow = { rowIndex: -1, row: [] };
const ROOT_ROW_HEIGHT = 32;
const noop = () => {};

const isNestedRow = (row: BodyRow): row is [string[], ...BodyRow[][]] =>
  Array.isArray(row[0]) && row.length > 1 && row.slice(1).every(Array.isArray);

const getLevelHeads = (data: TableData): HeadField[][][] =>
  data.config.head;

const getLevelTitles = (data: TableData, level: number): string[] =>
  data.config.titles?.[level] ?? [];

const getRowKey = (data: TableData, row: BodyRow, at: { level: number; index: number }, rowIndex: number): string => {
  const values = getMasterRow(row);
  const head = data.config.head?.[at.level]?.[at.index] ?? data.config.head;
  const primaryKeys = data.config.primaryKey?.[at.level]?.[at.index] ?? data.config.primaryKey[0][0];
  const keyValues = primaryKeys.map((key) => {
    const valueIndex = head.findIndex(([column]) => column === key);
    return valueIndex >= 0 ? values[valueIndex] ?? '' : '';
  });

  return keyValues.length > 0
    ? `${at.level}-${at.index}-${keyValues.join('\u001f')}`
    : `${at.level}-${at.index}-${rowIndex}-${values.join('\u001f')}`;
};

const Table = forwardRef<TableRef, TableDataProps>(
  ({
    data = EMPTY_TABLE_DATA,
    tableAt = {level: 0, index: 0},
    handleSelect = noop,
    handleRowValuesChanged,
    initialSelectedRowIndex = -1,
    rowsPerPage,
    onRowsPerPageChange,
    onReachEnd,
    onReachStart,
    onTrimTop,
    onTrimBottom,
    readonly = false,
  }, ref) => {
    const [selectedRow, setSelectedRow] = useState(initialSelectedRowIndex);
    const selectedRowRef = useRef(initialSelectedRowIndex);
    const [localRowsPerPage, setLocalRowsPerPage] = useState(50);
    const [open, setOpen] = useState(-1);
    const [editingCell, setEditingCell] = useState<{ row: number; column: number } | null>(null);
    const rootBodyRef = useRef<HTMLDivElement | null>(null);
    const prependScrollRef = useRef<{ height: number; top: number } | null>(null);
    const removeTopScrollRef = useRef<{ height: number; top: number } | null>(null);
    const lastScrollTopRef = useRef(0);
    const programmaticScrollRef = useRef(false);
    const downLoadTriggeredRef = useRef(false);
    const downTrimTriggeredRef = useRef(false);
    const upLoadTriggeredRef = useRef(false);
    const upTrimTriggeredRef = useRef(false);
    const levelHeads = getLevelHeads(data);

    useEffect(() => {
      selectedRowRef.current = initialSelectedRowIndex;
      setSelectedRow(initialSelectedRowIndex);
    }, [initialSelectedRowIndex]);

    useLayoutEffect(() => {
      const previous = prependScrollRef.current;
      const element = rootBodyRef.current;
      if (previous && element) {
        programmaticScrollRef.current = true;
        element.scrollTop = previous.top + (element.scrollHeight - previous.height);
        lastScrollTopRef.current = element.scrollTop;
        prependScrollRef.current = null;
      }

      const removedTop = removeTopScrollRef.current;
      if (removedTop && element) {
        programmaticScrollRef.current = true;
        element.scrollTop = Math.max(0, removedTop.top - (removedTop.height - element.scrollHeight));
        lastScrollTopRef.current = element.scrollTop;
        removeTopScrollRef.current = null;
      }
    }, [data]);

    const selectRow = useCallback((index: number) => {
      if (index < 0 || index >= data.body.length) {
        selectedRowRef.current = -1;
        setSelectedRow(-1);
        handleSelect(EMPTY_EDITED_ROW);
        setOpen(-1);
        return;
      }

      selectedRowRef.current = index;
      setSelectedRow(index);
      handleSelect({ rowIndex: index, row: data.body[index] });
      setOpen((previous) => (previous === index ? -1 : index));
    }, [data.body, handleSelect]);

    useImperativeHandle(ref, () => ({
      clearSelection: () => selectRow(-1),
      clearVisualSelection: () => {
        selectedRowRef.current = -1;
        setSelectedRow(-1);
        setOpen(-1);
        setEditingCell(null);
      },
      selectRow,
      setRow: (offset: number) => selectRow(selectedRowRef.current + offset),
      preserveScrollOnPrepend: () => {
        const element = rootBodyRef.current;
        if (element) prependScrollRef.current = { height: element.scrollHeight, top: element.scrollTop };
      },
      preserveScrollOnRemoveTop: () => {
        const element = rootBodyRef.current;
        if (element) removeTopScrollRef.current = { height: element.scrollHeight, top: element.scrollTop };
      },
    }), [selectRow]);

    const viewConfig = useCallback((head: HeadField[]) => head.reduce<Array<{
      index: number; key: string; title: string; width: number; type: string;
    }>>((result, [key, value], index) => {
      if (value.hidden) return result;
      const title = String(value.title ?? key.split('_').map((part) =>
        part.charAt(0).toUpperCase() + part.slice(1)).join(' '));
      const isPassword = key.toLowerCase().includes('password') || title.toLowerCase().includes('password');
      result.push({
        index,
        key,
        width: (value.width as number | undefined) ?? 100,
        type: isPassword ? 'password' : (value.type as string | undefined) ?? 'character varying',
        title,
      });
      return result;
    }, []), []);

    const updateRootRow = useCallback((value: string, column: number, rowIndex: number) => {
      const row = data.body[rowIndex];
      if (!row) return;

      const nextRow = isNestedRow(row)
        ? [[...row[0]], ...row.slice(1)] as BodyRow
        : [...row] as BodyRow;
      getMasterRow(nextRow)[column] = value;
      handleRowValuesChanged?.([{ rowIndex, row: nextRow }]);
    }, [data.body, handleRowValuesChanged]);

    const findRowIndex = useCallback((element: HTMLElement | null): number => {
      while (element) {
        if (element.dataset.idx) return Number(element.dataset.idx);
        element = element.parentElement;
      }
      return -1;
    }, []);

    const onTableClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const index = findRowIndex(target);
      if (index < 0) return;
      if (target.classList.contains('div-td') && !readonly) {
        setEditingCell({ row: index, column: Number(target.dataset.column) });
      }
      selectRow(index);
    }, [findRowIndex, readonly, selectRow]);

    const onRootScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      if (programmaticScrollRef.current) {
        programmaticScrollRef.current = false;
        lastScrollTopRef.current = element.scrollTop;
        return;
      }
      const scrollingDown = element.scrollTop > lastScrollTopRef.current;
      lastScrollTopRef.current = element.scrollTop;
      const viewportTop = element.getBoundingClientRect().top;
      const rowsAbove = Array.from(element.children).filter((child) =>
        child.classList.contains('div-tr') && child.getBoundingClientRect().bottom <= viewportTop,
      ).length;

      if (scrollingDown) {
        if (rowsAbove < 10) downLoadTriggeredRef.current = false;
        if (rowsAbove < 15) downTrimTriggeredRef.current = false;
        if (rowsAbove >= 10 && !downLoadTriggeredRef.current) {
          downLoadTriggeredRef.current = true;
          Promise.resolve(onReachEnd?.()).catch(() => {
            downLoadTriggeredRef.current = false;
          });
          return;
        }
        if (rowsAbove >= 15 && !downTrimTriggeredRef.current) {
          downTrimTriggeredRef.current = true;
          downLoadTriggeredRef.current = false;
          onTrimTop?.();
        }
      } else {
        if (rowsAbove > 10) upLoadTriggeredRef.current = false;
        if (rowsAbove > 5) upTrimTriggeredRef.current = false;
        if (rowsAbove <= 10 && !upLoadTriggeredRef.current) {
          upLoadTriggeredRef.current = true;
          Promise.resolve(onReachStart?.()).catch(() => {
            upLoadTriggeredRef.current = false;
          });
          return;
        }
        if (rowsAbove <= 5 && !upTrimTriggeredRef.current) {
          upTrimTriggeredRef.current = true;
          upLoadTriggeredRef.current = false;
          onTrimBottom?.();
        }
      }
    }, [onReachEnd, onReachStart, onTrimBottom, onTrimTop]);

    const renderTable = useCallback((rows: BodyRow[], at: { level: number; index: number }, nested = false): React.ReactNode => {
      const head = levelHeads[at.level]?.[at.index] ?? [];
      if (!head.length) return null;
      const columns = viewConfig(head);
      const title = getLevelTitles(data, at.level)[at.index];

      return (
        <div className={nested ? 'nested-table-block ms-3 mt-2' : 'table-component'} key={`${at.level}-${at.index}`}>
          {nested && title && <div className="small fw-semibold text-secondary mb-1">{title}</div>}
          <div className="div-table border rounded overflow-visible" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="div-thead table-dark">
              <div className="div-th d-flex bg-primary text-white fw-bold">
                {columns.map(({ key, title: columnTitle, width }) => (
                  <div key={key} className="div-td flex-fill p-1 border-end" style={{ width: `${width}px` }}>
                    {columnTitle}
                  </div>
                ))}
              </div>
            </div>
            <div className="div-tbody" onScroll={!nested ? onRootScroll : undefined}
              ref={!nested ? rootBodyRef : undefined}
              style={{ minHeight: 0, ...(!nested ? {
                height: `${20 * ROOT_ROW_HEIGHT}px`,
                maxHeight: `${20 * ROOT_ROW_HEIGHT}px`,
                overflowY: 'auto',
              } : {}) }}>
              {rows.map((row, rowIndex) => {
                const values = getMasterRow(row);
                const children = isNestedRow(row) ? row.slice(1) as BodyRow[][] : [];
                return (
                  <div className={`div-tr d-flex flex-column border-top${!nested && selectedRow === rowIndex ? ' row-selected' : ''}`} data-idx={!nested ? rowIndex : undefined} key={getRowKey(data, row, at, rowIndex)}>
                    <div className="div-subtr d-flex align-items-center" style={!nested ? { minHeight: `${ROOT_ROW_HEIGHT}px` } : undefined}>
                      {columns.map(({ index, key, width, type }) => (
                        <div key={key} className="div-td flex-fill px-1 border-end" data-column={index} style={{ width: `${width}px` }}>
                          {!nested && editingCell?.row === rowIndex && editingCell.column === index ? (
                            <InputExt type={type} className="w-100 border-0" value={values[index] ?? ''}
                              onChanged={(nextValue: string) => updateRootRow(nextValue, index, rowIndex)}
                              onBlur={() => setEditingCell(null)} />
                          ) : values[index] ?? ''}
                        </div>
                      ))}
                    </div>
                    {(nested || open === rowIndex) && children.map((childRows, childIndex) => (
                      <div key={childIndex} onClick={(event) => event.stopPropagation()}>
                        {renderTable(childRows, { level: at.level + 1, index: childIndex }, true)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }, [data, editingCell, levelHeads, onRootScroll, open, selectedRow, updateRootRow, viewConfig]);

    const rowsPerPageValue = rowsPerPage ?? localRowsPerPage;

    if (!Array.isArray(data.body) || !Array.isArray(data.config.head)) return null;

    return (
      <div className="position-relative" onClick={onTableClick}>
        {renderTable(data.body, tableAt,tableAt.level > 0)}
        <div className="table-pagination p-3 bg-light border-top flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <label className="small text-secondary" htmlFor="table-rows-per-page">Rows per page:</label>
            <select id="table-rows-per-page" className="form-select form-select-sm w-auto" value={rowsPerPageValue}
              onChange={(event) => {
                const value = Number(event.currentTarget.value);
                if (onRowsPerPageChange) {
                  onRowsPerPageChange(value);
                } else {
                  setLocalRowsPerPage(value);
                }
              }}>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
            </select>
          </div>
        </div>
      </div>
    );
  },
);

Table.displayName = 'Table';

export default Table;
